import { mensajeCamposInvalidos } from 'src/app/common/mensajes-validacion';
import { Component, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import {
  isValidEmailContactoLegacy,
  normalizarTextoLegacy,
  sanitizeNumbersOnly
} from 'src/app/common/input-utils';
import { BuscadorContactosFacade } from '../services/buscador-contactos.facade';
import { BuscadorContactosComponent } from '../buscador-contactos.component';

@Component({
  selector: 'app-buscador-contactos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BuscadorContactosComponent
  ],
  providers: [BuscadorContactosFacade],
  template: `
    <app-buscador-contactos-present
      [contactos]="facade.contactos()"
      [loading]="facade.loading()"
      [guardando]="facade.guardando()"
      [totalRecords]="facade.totalRecords()"
      [pageSize]="facade.pageSize()"
      [pageSizeOptions]="facade.pageSizeOptions"
      [currentPage]="facade.currentPage()"
      [totalPages]="facade.totalPages()"
      [hasNext]="facade.hasNext"
      [hasPrevious]="facade.hasPrevious"
      [(visible)]="visible"
      [(editingVisible)]="editingVisible"
      [contactForm]="contactForm"
      [editMode]="editMode"
      [editingContactId]="editingContactId"
      (selectContact)="onSelect($event)"
      (openCreate)="onOpenCreate()"
      (editContact)="onEdit($event)"
      (pageSizeChange)="onPageSizeChange($event)"
      (goToPage)="onGoToPage($event)"
      (saveContactEvent)="onSave($event)"
    ></app-buscador-contactos-present>
  `
})
export class BuscadorContactosContainer {
  private readonly fb = inject(FormBuilder);
  public readonly facade = inject(BuscadorContactosFacade);
  private readonly messageService = inject(MessageService);

  visible = false;
  editingVisible = false;
  editMode = false;
  editingContactId: number | null = null;
  contactForm!: FormGroup;
  @Output() contactoSeleccionado = new EventEmitter<any>();
  @Output() contactoCreado = new EventEmitter<any>();

  constructor() {
    this.initForm();
  }

  private initForm(): void {
    this.contactForm = this.fb.group({
      cdpersona: [0],
      dsnombre: ['', [Validators.required]],
      dscargo: [''],
      dstelefono: ['', [Validators.required]],
      dscelular: [''],
      // Legacy: solo REGEX_EMAILS (se valida al guardar con isValidEmailContactoLegacy), sin Validators.email
      dsemail: ['', [Validators.required]],
      nmconsecCentroTrabajo: [null]
    });
  }

  // External API used by parents. `cdpersona` may be omitted; the container will attempt to
  // derive it from the loaded contact list when creating a new contact.
  abrir(params?: {
    cdpersona?: number;
    nmconsecCentroTrabajo?: number;
    nmconsecOrden?: number;
  }) {
    this.visible = true;
    this.facade.abrir(params ?? undefined);
  }

  cerrar() {
    this.visible = false;
    this.facade.cerrar();
  }

  // Handlers wired to presentational component outputs
  onSelect(c: any) {
    // re-emit via DOM events if needed — presentational emits selectContact which container handles
    this.cerrar();
    this.contactoSeleccionado.emit(c);
  }

  onOpenCreate() {
    // Legacy (ListaContactosActivity): la persona es siempre la del beneficiario de la orden
    // (nmconsecutivo_beneficiario.getCdpersona()); nunca se deriva de la lista de contactos cargada, que
    // podría traer otra persona.
    const cdpersona = this.facade.params?.cdpersona;
    // Si hay nmconsecOrden, el backend resolverá cdpersona automáticamente (legacy: crearContactoCentroTrab)
    if ((!cdpersona || cdpersona <= 0) && !this.facade.params?.nmconsecOrden) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Contacto',
        detail:
          'No se ha determinado la persona. Abra el buscador desde la ficha de la persona o cargue contactos primero.'
      });
      return;
    }

    this.editMode = false;
    this.editingContactId = null;
    this.editingVisible = true;
    this.contactForm.reset({
      cdpersona: cdpersona,
      nmconsecCentroTrabajo: this.facade.params?.nmconsecCentroTrabajo ?? null
    });
  }

  onEdit(c: any) {
    this.editMode = true;
    this.editingContactId = c?.nmconsecutivo ?? null;
    this.editingVisible = true;
    this.contactForm.patchValue({
      cdpersona: c.cdpersona ?? this.facade.params?.cdpersona ?? 0,
      dsnombre: c.dsnombre || '',
      dscargo: c.dscargo || '',
      dstelefono: c.dstelefono || '',
      dscelular: c.dscelular || '',
      dsemail: c.dsemail || '',
      nmconsecCentroTrabajo: this.facade.params?.nmconsecCentroTrabajo ?? null
    });
  }

  async onSave(evt: any) {
    const f = evt; // container receives raw form value
    const req = {
      cdpersona: f.cdpersona ?? 0,
      cdrol: undefined,
      cdcargo: undefined,
      // Legacy (SearchBenefContactsViewImpl): Nombre y Cargo con Utilidades.cleanString + toUpperCase;
      // teléfonos solo dígitos (REGEX_JUST_NUMBERS)
      dsnombre: normalizarTextoLegacy(f.dsnombre).trim(),
      dscargo: normalizarTextoLegacy(f.dscargo).trim(),
      dstelefono: sanitizeNumbersOnly(f.dstelefono),
      dscelular: sanitizeNumbersOnly(f.dscelular),
      dsemail: f.dsemail,
      nmconsecCentroTrabajo: f.nmconsecCentroTrabajo ?? undefined
    };
    // Validate form validity (required fields + email)
    // Patch current contactForm values into the form before validation
    this.contactForm.patchValue(req, { emitEvent: false });
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      const etiquetas: Record<string, string> = {
        dsnombre: 'Nombre',
        dstelefono: 'Teléfono fijo',
        dsemail: 'Email'
      };
      const invalidos = Object.keys(etiquetas)
        .filter((campo) => this.contactForm.get(campo)?.invalid)
        .map((campo) => etiquetas[campo]);
      this.messageService.add({
        severity: 'warn',
        summary: 'Contacto',
        detail: mensajeCamposInvalidos(invalidos)
      });
      return;
    }

    // additional email sanity check
    // Legacy: widgetEmail.setRegex(Constants.REGEX_EMAILS)
    if (req.dsemail && !isValidEmailContactoLegacy(req.dsemail)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Contacto',
        detail: mensajeCamposInvalidos(['Email'])
      });
      return;
    }

    // Ensure cdpersona is valid before attempting save (unless nmconsecOrden is set — backend resolves it)
    if (
      (!req.cdpersona || req.cdpersona <= 0) &&
      !this.facade.params?.nmconsecOrden
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Contacto',
        detail: 'Persona inválida. No se puede guardar el contacto.'
      });
      return;
    }

    const created = await this.facade.saveContact(
      req.cdpersona,
      this.editMode,
      this.editingContactId ?? undefined,
      req
    );
    // Si el guardado falló se mantiene el diálogo abierto para no perder lo digitado
    if (!this.facade.ultimoGuardadoExitoso) return;
    this.editingVisible = false;
    this.editMode = false;
    this.editingContactId = null;
    if (created) this.contactoCreado.emit(created);
  }

  // Deletion handled elsewhere; removed from buscador container to match legacy behaviour

  onPageSizeChange(v: number) {
    this.facade.pageSize.set(v);
    this.facade.currentPage.set(1);
    this.facade.first.set(0);
    this.facade.loadPage(0, this.facade.pageSize());
  }

  onGoToPage(page: number) {
    // frontend-girop §17.7: ignorar la navegación mientras carga (un doble clic no avanza dos páginas)
    if (this.facade.loading()) return;
    const start = (page - 1) * this.facade.pageSize();
    // No fijar currentPage aquí: loadPage navega por cursor comparando la página pedida con la
    // actual y es quien actualiza currentPage al terminar (si se fija antes, ninguna rama aplica
    // y la página queda vacía).
    this.facade.first.set(start);
    this.facade.loadPage(start, this.facade.pageSize());
  }
}
